import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  GuestGetBookedMeetingResponseV2,
  GuestGetBookedMeetingResponseV2Interface,
  GuestService,
} from '@vendasta/meetings';
import { resolveBookingSource } from '../booking-request-id';
import { Observable, of, Subscription } from 'rxjs';
import { catchError, map, retry, shareReplay, take } from 'rxjs/operators';
import { BookMeNowStoreService } from '../book-me-now-store.service';
import {
  buildDisplayTitle,
  extractEventTypeName,
  findBatchMeetingById,
  formatDate,
  formatDuration,
  formatNames,
  formatTimeRange,
  isBatchBooking,
  isInPersonMeeting,
} from '../util';

type CancelBatchMeetingVm = {
  meetingId: string;
  eventTypeName: string;
  location: string;
  isInPerson: boolean;
};

type CancelInfoVm = {
  meetingInfo: GuestGetBookedMeetingResponseV2;
  displayTitle: string;
  hostNamesText: string;
  dateText: string;
  timeRangeText: string;
  durationText: string;
  isInPerson: boolean;
  timezoneZoneText: string;
  isBatch: boolean;
  meetings: CancelBatchMeetingVm[];
};

type GuestBookedMeetingV2WithTimezone = GuestGetBookedMeetingResponseV2 & {
  timezone?: string | { zone?: string };
  timeZone?: { id?: string };
};

@Component({
  selector: 'app-cancel-info-page',
  templateUrl: './cancel-info.component.html',
  styleUrls: ['./cancel-info.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CancelInfoPageComponent implements OnInit, OnDestroy {
  meetingId!: string;
  calendarId!: string;
  authToken!: string;
  meetingInfo$: Observable<GuestGetBookedMeetingResponseV2>;
  cancelInfoVm$: Observable<CancelInfoVm | null>;
  cancellationReason = '';
  error = false;
  isCancelling = false;

  private subscriptions: Subscription[] = [];

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    public readonly bookMeNowStoreService: BookMeNowStoreService,
    private readonly guestService: GuestService
  ) {}

  ngOnInit(): void {
    this.meetingId = this.route.snapshot.params.meetingId;
    this.calendarId = this.route.snapshot.params.calendarId;
    this.authToken = this.route.snapshot.queryParams.authToken;

    this.meetingInfo$ = this.bookMeNowStoreService
      .getGuestBookedMeetingV2({ id: this.meetingId })
      .pipe(
        catchError((err) => {
          this.bookMeNowStoreService.handleMeetingError(err, 'cancel');
          return of(null);
        })
      );

    this.cancelInfoVm$ = this.meetingInfo$.pipe(
      map((meetingInfo) => {
        if (!meetingInfo) {
          return null;
        }

        // Extract authToken from cancelLink if not already set
        if (!this.authToken && meetingInfo.cancelLink) {
          try {
            const cancelUrl = new URL(meetingInfo.cancelLink);
            this.authToken = cancelUrl.searchParams.get('authToken') || '';
          } catch {
            // Invalid URL, ignore
          }
        }

        const meetingInfoWithTimezone =
          meetingInfo as GuestBookedMeetingV2WithTimezone;
        const timezoneRegion =
          typeof meetingInfoWithTimezone.timezone === 'string'
            ? meetingInfoWithTimezone.timezone
            : meetingInfoWithTimezone.timeZone?.id;
        const timezoneZoneText =
          typeof meetingInfoWithTimezone.timezone === 'object'
            ? meetingInfoWithTimezone.timezone?.zone || ''
            : '';

        const isInPerson = isInPersonMeeting(meetingInfo.meetingLocationType);

        const responseWithBatch =
          meetingInfo as unknown as GuestGetBookedMeetingResponseV2Interface;
        const isBatch = isBatchBooking(responseWithBatch);

        // When the URL identifies one meeting in a batch, show only that
        // meeting's event-type name instead of comma-joining the whole batch.
        const matchedBatchMeeting = isBatch
          ? findBatchMeetingById(responseWithBatch.meetings, this.meetingId)
          : null;

        const displayTitle = matchedBatchMeeting
          ? extractEventTypeName(matchedBatchMeeting.title || '')
          : buildDisplayTitle(
              meetingInfo.title || '',
              isBatch,
              responseWithBatch.meetings || []
            );

        return {
          meetingInfo,
          displayTitle,
          hostNamesText: formatNames(meetingInfo.hostUserNames || []),
          dateText: formatDate(meetingInfo.startTime),
          timeRangeText: formatTimeRange(
            meetingInfo.startTime,
            meetingInfo.endTime,
            timezoneRegion
          ),
          durationText: formatDuration(
            meetingInfo.startTime,
            meetingInfo.endTime
          ),
          isInPerson: isBatch
            ? (responseWithBatch.meetings || []).some((m: any) => isInPersonMeeting(m.meetingLocationType))
            : isInPerson,
          timezoneZoneText,
          isBatch,
          meetings: isBatch
            ? (responseWithBatch.meetings || []).map((m: any) => ({
                meetingId: m.meetingId || '',
                eventTypeName: extractEventTypeName(m.title || ''),
                location: m.location || '',
                isInPerson: isInPersonMeeting(m.meetingLocationType),
              }))
            : [],
        };
      }),
      shareReplay(1)
    );
  }

  cancelAppointment(): void {
    this.isCancelling = true;
    const subscription = this.guestService
      .cancelBatchMeeting({
        id: this.meetingId,
        cancellationReason: this.cancellationReason,
        bookingSource: resolveBookingSource(),
      })
      .pipe(retry(1), take(1))
      .subscribe(
        () => {
          this.error = false;
          this.isCancelling = false;
          this.router.navigate(['cancelled'], {
            relativeTo: this.route,
          });
        },
        (err) => {
          this.isCancelling = false;
          if (err.status === 404) {
            this.router.navigate(['cancelled'], {
              relativeTo: this.route,
            });
          } else {
            this.bookMeNowStoreService.handleMeetingError(err, 'cancel');
          }
        }
      );
    this.subscriptions.push(subscription);
  }

  rescheduleInstead(): void {
    const subscription = this.cancelInfoVm$.pipe(take(1)).subscribe((vm) => {
      if (vm?.meetingInfo?.rescheduleLink) {
        window.location.href = vm.meetingInfo.rescheduleLink;
      }
    });
    this.subscriptions.push(subscription);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }
}
